import {timeInterval} from 'rxjs';
import Decimal from './break_infinity.js';
import {Panel} from './panel.js';
import {Resource} from './resource';
import {
  performActivityFromQueue,
  ri,
  save,
  tickLength,
  updateSpellCooldowns,
  updateUnlocks,
} from './runner-idle';
import {Stat} from './stat';
import {NamedValue} from './types/named-value.js';
import {ResourceName, StatName} from './types/runner-idle.js';

let canUseTime = false;
let oldStats: NamedValue<StatName>[] = [];
let oldResources: NamedValue<ResourceName>[] = [];
let lastSaved = Date.now();

export function gameLoop(deltaTimeMs: number) {
  // Save time when paused
  if (ri.gameSpeed === 0) {
    ri.resources.time.addValue(deltaTimeMs / 1000);
    return;
  }
  // Using saved time? Then we need to update the time resource
  if (ri.useSavedTime && ri.resources.time.value.gt(0)) {
    ri.resources.time.setValue(ri.resources.time.value.minus(1));
  }
  // Update overall masonry layout
  if (!ri.masonryRoot) {
    ri.masonryRoot = ri.shadowRoot?.getElementById('masonry-root') ?? undefined;
  }
  if (ri.masonryRoot && !ri.masonry) {
    ri.RefreshMasonry();
    if (ri.newGame) {
      ri.loadPanels();
    } else {
      const panelsString = localStorage.getItem('panels');
      if (panelsString) {
        const panels = JSON.parse(panelsString);
        ri.loadPanels(panels);
        setTimeout(() => {
          for (const panel of ri.masonryRoot?.querySelectorAll(
            '#masonry-root > *'
          ) as NodeListOf<Panel>) {
            panel.new = false;
          }
        }, 1000);
      }
    }
    ri.readyToSave = true;
  }
  if (ri.steppingOneFrame) {
    ri.steppingOneFrame = false;
    ri.gameSpeed = 0;
  }
  ri.tick$.next(ri.tick$.value.plus(deltaTimeMs / 1000));
  updateSpellCooldowns(deltaTimeMs);
  if (!canUseTime && ri.resources.time.unlocked) {
    const useSavedTimeToggle = ri
      .getPanel('speed-controls')
      .shadowRoot?.getElementById('use-saved-time-toggle');
    if (useSavedTimeToggle) {
      canUseTime = true;
      useSavedTimeToggle.hidden = false;
      if (!ri.isDragging) ri.masonry?.shiftLayout();
    }
  }

  // If there're no activities to do, pause the game // add a rest
  if (
    !ri.activityQueue.some(
      (queueItem) =>
        queueItem.repeat > 0 &&
        queueItem.activity.canAfford(-1, queueItem.optionIndex, 1, true)
    )
  ) {
    ri.lastGameSpeed = ri.gameSpeed;
    ri.gameSpeed = 0;
    return;
  }
  const atStart = () => {
    ri.activityQueue.forEach((queueItem, i) => {
      if (i !== ri.activityQueueIndex) queueItem.activeIndex = 0;
    });
    ri.lastQueueLoopDelta = ri.activityQueueDelta;
    ri.lastDistance = ri.distance;
    ri.lastTime = ri.tick;
    ri.lastResources = Object.fromEntries(
      Object.values(ri.resources).map((r) => [
        r.name,
        {
          value: r.value,
          capacity: r.capacity.isFinite() ? r.capacity : new Decimal(0),
        },
      ])
    );
    ri.lastStats = Object.fromEntries(
      Object.values(ri.stats).map((s) => [
        s.name,
        {
          value: s.value,
        },
      ])
    );
    ri.getPanel('resources-delta').requestUpdate();
  };

  // Player actions
  const performed = performActivityFromQueue(deltaTimeMs, ri, atStart);

  // Status effects
  ri.statusEffects.forEach((statusEffect) =>
    statusEffect.perform(new Decimal(1))
  );

  // Actor actions
  // ri.actors.forEach((actor, i) =>
  //   ri.actors.forEach((otherActor, j) => {
  //     if (j <= i) return;
  //     const distance = actor.position.distance(otherActor.position);
  //     if (distance.lte(actor.interactionRange)) {
  //       actor.interact(distance, otherActor);
  //     }
  //     if (distance.lte(otherActor.interactionRange)) {
  //       otherActor.interact(distance, actor);
  //     }
  //   })
  // );

  updateUnlocks();

  // Do all Resource.prototype.onTick()s
  for (const resource of Object.values(ri.resources)) {
    resource.onTick(performed, ri.activeActivity!);
  }

  updateUnlocks();

  /* Set resources gained this frame (deltas) */
  // TODO TEMP
  // for (const oldStat of oldStats) {
  //   ri.stats[oldStat.name].delta = ri.stats[oldStat.name].value.minus(
  //     oldStat.value
  //   );
  // }
  // for (const oldResource of oldResources) {
  //   ri.resources[oldResource.name].delta = ri.resources[
  //     oldResource.name
  //   ].value.minus(oldResource?.value ?? 0);
  // }
  /* Keep a copy of the current values to calculate deltas next time*/
  oldStats = (Object.values(ri.stats) as Stat[]).map((stat: Stat) => ({
    value: stat.value,
    name: stat.name,
  }));
  oldResources = (Object.values(ri.resources) as Resource[]).map(
    (resource: Resource) => ({
      value: resource.value,
      name: resource.name,
    })
  );

  const now = Date.now();
  if (lastSaved < now - 1 * 1000) {
    lastSaved = now;
    save();
  }

  ri.updateMap();
}
